import type { KdsTicket } from '../types';
import type { ConnectionState } from '../hooks/useKdsStore';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * 临时菜品立宪 2026-08-01 §2.6:`external_id` 是 `Option<i64>` —— 临时行没有
 * 商品编号,wire 上就是 `null`。
 *
 * 原实现对每个 item 强制数字,否则 throw。这条 throw 走在 `requireKdsTicket`
 * 里,而它在渲染路径上 —— 一张含临时菜的厨房小票会让**整个 KDS 屏幕**抛异常
 * 炸掉。现在 `null` 是合法值,只有"存在但不是有限数字"才是真的坏 payload。
 */
function requireExternalIdShape(value: unknown, context: string): void {
  if (value === null || value === undefined) return;
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new TypeError(`${context} has a non-numeric external_id`);
  }
}

export function requireKdsTicket(value: unknown, context: string): KdsTicket {
  if (!isRecord(value)) {
    throw new TypeError(`${context} missing ticket`);
  }
  if (!Array.isArray(value.items)) {
    throw new TypeError(`${context} missing items`);
  }

  value.items.forEach((item, index) => {
    if (!isRecord(item)) {
      throw new TypeError(`${context}.items[${index}] missing item`);
    }
    requireExternalIdShape(item.external_id, `${context}.items[${index}].external_id`);
  });

  return value as KdsTicket;
}

export function requireKdsTickets(value: unknown, context: string): KdsTicket[] {
  if (!Array.isArray(value)) {
    throw new TypeError(`${context} missing tickets`);
  }
  return value.map((ticket, index) => requireKdsTicket(ticket, `${context}[${index}]`));
}

export function requireKdsHistoryTickets(value: unknown, context: string): KdsTicket[] {
  if (!Array.isArray(value)) {
    throw new TypeError(`${context} missing history tickets`);
  }
  return value.map((entry, index) => {
    if (!isRecord(entry)) {
      throw new TypeError(`${context}[${index}] missing history entry`);
    }
    return requireKdsTicket(entry.ticket, `${context}[${index}].ticket`);
  });
}

export function requireKdsConnectionState(value: unknown, context: string): ConnectionState {
  if (value === 'connected' || value === 'reconnecting' || value === 'disconnected') {
    return value;
  }
  throw new TypeError(`${context} invalid connection state`);
}
